import { ResponsiveTable } from '../components/ResponsiveTable';
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  User,
  Mail,
  AlertCircle,
  Loader2,
  Filter,
  ChevronDown,
  KeyRound,
} from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { HelpIcon } from '../components/HelpIcon';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { ConfirmHardPasswordResetModal } from '../components/ConfirmHardPasswordResetModal';
import { useRbac } from '../contexts/RbacContext';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useToast } from '../components/Toast';
import { RootState } from '../store';

interface UserListItem {
  id: string;
  email: string;
  userType: string;
  status: string;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  client?: { id: string; firstName: string; lastName: string } | null;
  admin?: { id: string; name: string; role: string } | null;
  practitioner?: { id: string; firstName: string; lastName: string } | null;
}

interface UsersResponse {
  success: boolean;
  data: {
    users: UserListItem[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export function UsersListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { can } = useRbac();
  const canHardDelete = can('entities:delete:hard');
  const { showToast, ToastComponent } = useToast();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = String(currentUser?.role || '').toLowerCase().replace(/-/g, '_') === 'super_admin';
  const [passwordResetTarget, setPasswordResetTarget] = useState<{ id: string; email: string } | null>(null);

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; userIds: string[] }>({
    isOpen: false,
    userIds: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; userIds: string[] }>({
    isOpen: false,
    userIds: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);

  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, search, userTypeFilter, statusFilter, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
      });
      if (search) params.set('search', search);
      if (userTypeFilter) params.set('userType', userTypeFilter);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`${getApiUrl('/api/admin/users')}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: UsersResponse = await response.json();
        setUsers(data.data.users);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        const data = await response.json().catch(() => ({}));
        showToast(data.message || 'Impossible de charger les utilisateurs', 'error', 7000);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast('Impossible de charger les utilisateurs', 'error', 7000);
    } finally {
      setLoading(false);
    }
  };

  const visibleUserIds = useMemo(() => users.map((u) => u.id), [users]);
  const {
    selectedIds: selectedUserIds,
    selectedCount,
    selectedArray: selectedUserArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleUserIds);

  const openBulkDeleteConfirm = () => {
    if (selectedUserIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, userIds: selectedUserArray });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, userIds: [] });
  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.userIds.length === 0) return;
    try {
      setBulkDeleting(true);
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failureMessage: string | null = null;
      for (const id of bulkDeleteConfirm.userIds) {
        const response = await fetch(getApiUrl(`/api/admin/users/${id}`), {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          successCount += 1;
          continue;
        }
        const data = await response.json().catch(() => ({}));
        failureMessage = data.message || 'Erreur lors de la suppression';
      }
      setBulkDeleteConfirm({ isOpen: false, userIds: [] });
      clearSelection();
      await fetchUsers();
      if (successCount > 0) {
        showToast(`${successCount} utilisateur(s) supprimé(s)`, 'success');
      }
      if (failureMessage) {
        showToast(failureMessage, 'error', 7000);
      }
    } catch (error) {
      console.error('Bulk delete users error:', error);
      showToast('Impossible de supprimer les utilisateurs', 'error', 7000);
    } finally {
      setBulkDeleting(false);
    }
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedUserIds.size === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, userIds: selectedUserArray });
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, userIds: [] });
  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.userIds.length === 0) return;
    try {
      setBulkHardDeleting(true);
      const token = localStorage.getItem('token');
      let successCount = 0;
      let failureMessage: string | null = null;
      for (const id of bulkHardDeleteConfirm.userIds) {
        const response = await fetch(getApiUrl(`/api/admin/users/${id}?hardDelete=true`), {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          successCount += 1;
          continue;
        }
        const data = await response.json().catch(() => ({}));
        failureMessage = data.message || 'Erreur lors de la suppression définitive';
      }
      setBulkHardDeleteConfirm({ isOpen: false, userIds: [] });
      clearSelection();
      await fetchUsers();
      if (successCount > 0) {
        showToast(`${successCount} utilisateur(s) supprimé(s) définitivement`, 'success');
      }
      if (failureMessage) {
        showToast(failureMessage, 'error', 7000);
      }
    } catch (error) {
      console.error('Bulk hard delete users error:', error);
      showToast('Impossible de supprimer définitivement les utilisateurs', 'error', 7000);
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case 'ADMIN': return 'Admin';
      case 'CLIENT': return 'Client';
      case 'PRACTITIONER': return 'Praticien';
      default: return userType;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Actif';
      case 'INACTIVE': return 'Inactif';
      case 'SUSPENDED': return 'Suspendu';
      case 'LOCKED': return 'Verrouillé';
      default: return status;
    }
  };

  const getLinkedEntity = (u: UserListItem) => {
    if (u.admin) return `Admin: ${u.admin.name}`;
    if (u.client) return `Client: ${u.client.firstName} ${u.client.lastName}`;
    if (u.practitioner) return `Praticien: ${u.practitioner.firstName} ${u.practitioner.lastName}`;
    return '—';
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <ToastComponent />
      <div className="dt-list-toolbar flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <User className="w-6 h-6" />
            <span>Utilisateurs</span>
            <HelpIcon docPath="users" tooltip="Liste des comptes utilisateur" />
          </h1>
          <p className="text-gray-600 mt-1">{total} utilisateur{total !== 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/users/create">
            <button className="btn-primary inline-flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              Créer un utilisateur
            </button>
          </Link>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={openBulkDeleteConfirm}
                disabled={bulkDeleting || bulkHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDeleteConfirm}
                  disabled={bulkDeleting || bulkHardDeleting}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Suppression définitive"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters flex flex-nowrap items-center gap-3">
          <div className="relative flex-1 min-w-0 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher par email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={userTypeFilter}
            onChange={(e) => { setUserTypeFilter(e.target.value); setPage(1); }}
            className="input-field shrink-0 w-[180px]"
          >
            <option value="">Tous les types</option>
            <option value="CLIENT">Client</option>
            <option value="ADMIN">Admin</option>
            <option value="PRACTITIONER">Praticien</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="input-field shrink-0 w-[180px]"
          >
            <option value="">Tous les statuts</option>
            <option value="ACTIVE">Actif</option>
            <option value="INACTIVE">Inactif</option>
            <option value="SUSPENDED">Suspendu</option>
            <option value="LOCKED">Verrouillé</option>
          </select>
          <button
            onClick={() => {
              setSearch('');
              setUserTypeFilter('');
              setStatusFilter('');
              setSortKey('createdAt');
              setSortDir('desc');
              setPage(1);
            }}
            className="btn-secondary flex items-center justify-center space-x-2 shrink-0"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={users.length === 0 || bulkDeleting || bulkHardDeleting}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Email</span>
                    {sortKey === 'email' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('userType')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {sortKey === 'userType' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'status' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profil lié</th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id} className="group hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(u.id)}
                      disabled={bulkDeleting || bulkHardDeleting}
                      onChange={() => toggleSelect(u.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      {u.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {getUserTypeLabel(u.userType)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      u.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                      u.status === 'INACTIVE' ? 'bg-gray-100 text-gray-800' :
                      u.status === 'SUSPENDED' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {getStatusLabel(u.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getLinkedEntity(u)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(u.createdAt).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Voir"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/users/${u.id}/edit`)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {isSuperAdmin && (
                        <button
                          onClick={() => setPasswordResetTarget({ id: u.id, email: u.email })}
                          className="text-amber-600 hover:text-amber-800 p-1"
                          title={t('users.passwordReset.action')}
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {!loading && users.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucun utilisateur trouvé.
          </div>
        )}

        {totalPages > 1 && (
          <div className="dt-list-toolbar px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Page {page} sur {totalPages} ({(page - 1) * limit + 1}-{Math.min(page * limit, total)} sur {total})
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Soft delete confirmation */}
      {bulkDeleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer les utilisateurs</h3>
            <p className="text-gray-600 mb-4">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.userIds.length}</strong> utilisateur{bulkDeleteConfirm.userIds.length !== 1 ? 's' : ''} ?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={cancelBulkDelete} disabled={bulkDeleting} className="btn-secondary">
                Annuler
              </button>
              <button onClick={confirmBulkDelete} disabled={bulkDeleting} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={bulkHardDeleteConfirm.userIds.length !== 1 ? 'utilisateur(s)' : 'utilisateur'}
        count={bulkHardDeleteConfirm.userIds.length}
        isLoading={bulkHardDeleting}
      />

      <ConfirmHardPasswordResetModal
        isOpen={!!passwordResetTarget}
        userId={passwordResetTarget?.id ?? null}
        userEmail={passwordResetTarget?.email ?? ''}
        onClose={() => setPasswordResetTarget(null)}
        onSuccess={() => {
          setPasswordResetTarget(null);
          showToast(t('users.passwordReset.success'), 'success');
        }}
      />
    </div>
  );
}
